"use client";

import { Modal, Row, Col, Form } from "react-bootstrap";
import Button from "../Button/Button";
import { useForm, Controller } from "react-hook-form";
import { useSubmitForm } from "@/hooks/useSubmitForm";
import { useRouter } from "next/navigation";
import { IoIosArrowDown } from "react-icons/io";
import "./ContactFormModal.scss";
import PhoneInput from "react-phone-input-2";

const ContactFormModal = ({ show, onClose }) => {
  const router = useRouter();
  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      phoneNumber: "+91",
      industryType: "",
    },
  });
  const { submitRequest, loading } = useSubmitForm();

  // const industryOptions = [
  //   "Manufacturing",
  //   "Retail",
  //   "Wholesale & Distribution",
  //   "Service / Maintenance",
  //   "Construction / Real Estate",
  //   "Logistics & Transportation",
  //   "Others",
  // ];
  const industryOptions = [
    "Manufacturing",
    "Information Technology & Services",
    "Healthcare & Pharmaceuticals",
    "Finance, Banking & Insurance",
    "Retail & E-Commerce",
    "Construction & Real Estate",
    "Automotive & Transportation",
    "Education & Training",
    "Telecommunications",
    "Energy & Utilities",
    "Professional Services & Consulting",
    "Other",
  ];

  const onSubmit = (formData) => {
    submitRequest("/contact/", formData, () => {
      reset();
      onClose();
      router.push("/thank-you");
    });
  };

  const handleCancel = () => {
    reset();
    onClose();
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      size="lg"
      contentClassName="modal-zoom"
      className="contact-form-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>Contact Us</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form
          id="contact-form"
          className="form-container"
          onSubmit={handleSubmit(onSubmit)}
        >
          <Row>
            <Col xs={12} md={6}>
              <Form.Group className="mb-4">
                <Form.Label htmlFor="contact-name">
                  Name<span className="required">*</span>
                </Form.Label>
                <Form.Control
                  id="contact-name"
                  type="text"
                  autoComplete="name"
                  placeholder="Enter your name"
                  {...register("name", {
                    required: "Name is required",
                    minLength: {
                      value: 2,
                      message: "Minimum 2 characters",
                    },
                    maxLength: {
                      value: 30,
                      message: "Maximum 30 characters allowed",
                    },
                    pattern: {
                      value: /^[A-Za-z ]+$/,
                      message: "Name must contain only letters and spaces",
                    },
                  })}
                />
                {errors.name && (
                  <p className="error-message">{errors.name.message}</p>
                )}
              </Form.Group>
            </Col>

            <Col xs={12} md={6}>
              <Form.Group className="mb-4">
                <Form.Label htmlFor="contact-phone">
                  Mobile Number <span className="required">*</span>
                </Form.Label>

                <Controller
                  name="phoneNumber"
                  control={control}
                  rules={{
                    required: "Mobile number is required",
                    pattern: {
                      value: /^\+[1-9][0-9]{9,14}$/,
                      message: "Enter valid phone number with country code",
                    },
                  }}
                  render={({ field }) => (
                    <PhoneInput
                      country="in"
                      value={field.value}
                      onChange={(value, country) => {
                        const dialCode = `+${country.dialCode}`;

                        if (!value.startsWith(country.dialCode)) {
                          field.onChange(dialCode);
                          return;
                        }

                        if (!field.value?.startsWith(dialCode)) {
                          field.onChange(dialCode);
                        } else {
                          field.onChange(`+${value.replace(/\D/g, "")}`);
                        }
                      }}
                      onCountryChange={(country) =>
                        field.onChange(`+${country.dialCode}`)
                      }
                      inputClass="phone-input"
                      countryCodeEditable={false}
                      containerClass="phone-input-container"
                      dropdownClass="phone-dropdown"
                      inputProps={{
                        id: "contact-phone",
                        name: "phoneNumber",
                        autoComplete: "tel",
                        required: true,
                        "aria-label": "Mobile Number",
                      }}
                    />
                  )}
                />

                {errors.phoneNumber && (
                  <p className="error-message">{errors.phoneNumber.message}</p>
                )}
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col xs={12} md={6}>
              <Form.Group className="mb-4">
                <Form.Label htmlFor="contact-email">
                  Email Address <span className="required">*</span>
                </Form.Label>
                <Form.Control
                  id="contact-email"
                  type="email"
                  autoComplete="name"
                  placeholder="Enter your email address"
                  {...register("email", {
                    required: "Email is required",
                    pattern: {
                      value: /^[a-zA-Z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i,
                      message: "Invalid email address",
                    },
                  })}
                />
                {errors.email && (
                  <p className="error-message">{errors.email.message}</p>
                )}
              </Form.Group>
            </Col>

            <Col xs={12} md={6}>
              <Form.Group className="mb-4">
                <Form.Label htmlFor="contact-company">
                  Company Name <span className="required">*</span>
                </Form.Label>
                <Form.Control
                  id="contact-company"
                  type="text"
                  placeholder="Enter your company name"
                  {...register("company", {
                    required: "Company name is required",
                    minLength: {
                      value: 2,
                      message: "Minimum 2 characters required",
                    },
                    maxLength: {
                      value: 100,
                      message: "Company name cannot exceed 100 characters",
                    },
                    pattern: {
                      value: /^[A-Za-z0-9][A-Za-z0-9\s&.,'()/-]*$/,
                      message: "Company name contains invalid characters",
                    },
                  })}
                />
                {errors.company && (
                  <p className="error-message">{errors.company.message}</p>
                )}
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col xs={12} md={6}>
              <Form.Group controlId="formIndustry" className="mb-4">
                <Form.Label>
                  What is your Industry Type?{" "}
                  <span className="required">*</span>
                </Form.Label>
                <div className="select-wrapper">
                  <Form.Control
                    as="select"
                    className={`industry-select ${
                      !watch("industryType")
                        ? "placeholder-active"
                        : "value-selected"
                    }`}
                    {...register("industryType", {
                      required: "Industry type is required",
                    })}
                  >
                    <option value="" disabled>
                      Select Your Industry Type
                    </option>

                    {industryOptions.map((industry) => (
                      <option key={industry} value={industry}>
                        {industry}
                      </option>
                    ))}
                  </Form.Control>

                  <span className="select-icon">
                    <IoIosArrowDown />
                  </span>
                </div>
                {errors.industryType && (
                  <p className="error-message">{errors.industryType.message}</p>
                )}
              </Form.Group>
            </Col>

            <Col xs={12} md={6}>
              <Form.Group className="mb-4">
                <Form.Label htmlFor="contact-location">
                  Location <span className="required">*</span>
                </Form.Label>
                <Form.Control
                  id="contact-location"
                  type="text"
                  placeholder="Enter your company location"
                  {...register("location", {
                    required: "Company Location is required",
                    minLength: {
                      value: 2,
                      message: "Minimum 2 characters required",
                    },
                    maxLength: {
                      value: 100,
                      message: "Location cannot exceed 100 characters",
                    },
                    pattern: {
                      value: /^[A-Za-z0-9][A-Za-z0-9\s&.,'()/-]*$/,
                      message: "Location contains invalid characters",
                    },
                  })}
                />
                {errors.location && (
                  <p className="error-message">{errors.location.message}</p>
                )}
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-4">
            <Form.Label htmlFor="contact-message">
              Message <span className="required">*</span>
            </Form.Label>
            <Form.Control
              id="contact-message"
              as="textarea"
              rows={3}
              placeholder="Enter your message"
              className="no-resize"
              {...register("message", {
                required: "Message is required",
                minLength: {
                  value: 10,
                  message: "Minimum 10 characters required",
                },
                maxLength: {
                  value: 500,
                  message: "Message cannot exceed 500 characters",
                },
              })}
            />
            {errors.message && (
              <p className="error-message">{errors.message.message}</p>
            )}
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="tertiary" size="lg" onClick={handleCancel}>
          Cancel
        </Button>
        <Button
          form="contact-form"
          variant="primary"
          size="lg"
          type="submit"
          disabled={loading}
        >
          {loading ? "Submitting..." : "Submit"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ContactFormModal;
